import type { Bounds, Camera, Matrix2D, Rect, Size, Vec2 } from '@/types';
import { CAMERA_LIMITS } from '@/constants/camera';

/**
 * World ↔ screen mapping. See docs/coordinate-system.md.
 *
 *   screen = (world − camera.position) · zoom
 *   world  = screen / zoom + camera.position
 */
export const worldToScreen = (world: Vec2, camera: Camera): Vec2 => ({
  x: (world.x - camera.position.x) * camera.zoom,
  y: (world.y - camera.position.y) * camera.zoom,
});

export const screenToWorld = (screen: Vec2, camera: Camera): Vec2 => ({
  x: screen.x / camera.zoom + camera.position.x,
  y: screen.y / camera.zoom + camera.position.y,
});

export const clampZoom = (zoom: number): number =>
  Math.min(CAMERA_LIMITS.maxZoom, Math.max(CAMERA_LIMITS.minZoom, zoom));

/** Pans the camera by a delta expressed in screen pixels. */
export const panCameraByScreenDelta = (camera: Camera, screenDelta: Vec2): Camera => ({
  ...camera,
  position: {
    x: camera.position.x - screenDelta.x / camera.zoom,
    y: camera.position.y - screenDelta.y / camera.zoom,
  },
});

/**
 * Zooms so that the world point under `screenAnchor` stays fixed on screen.
 * This is the "zoom towards cursor" behaviour users expect.
 */
export const zoomCameraAt = (camera: Camera, nextZoom: number, screenAnchor: Vec2): Camera => {
  const zoom = clampZoom(nextZoom);
  if (zoom === camera.zoom) return camera;
  const anchorWorld = screenToWorld(screenAnchor, camera);
  return {
    zoom,
    position: {
      x: anchorWorld.x - screenAnchor.x / zoom,
      y: anchorWorld.y - screenAnchor.y / zoom,
    },
  };
};

/** World-space rectangle currently visible through the viewport. */
export const visibleWorldBounds = (camera: Camera, viewport: Size): Bounds => {
  const min = screenToWorld({ x: 0, y: 0 }, camera);
  const max = screenToWorld({ x: viewport.width, y: viewport.height }, camera);
  return { minX: min.x, minY: min.y, maxX: max.x, maxY: max.y };
};

/** Camera that centres the given world bounds in the viewport with some padding. */
export const cameraFittingBounds = (bounds: Bounds, viewport: Size, padding = 64): Camera => {
  const w = Math.max(1, bounds.maxX - bounds.minX);
  const h = Math.max(1, bounds.maxY - bounds.minY);
  const zoom = clampZoom(Math.min((viewport.width - padding * 2) / w, (viewport.height - padding * 2) / h));
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  return {
    zoom,
    position: {
      x: centerX - viewport.width / (2 * zoom),
      y: centerY - viewport.height / (2 * zoom),
    },
  };
};

/** Matrix that maps world coordinates to screen coordinates for the given camera. */
export const cameraMatrix = (camera: Camera): Matrix2D => ({
  a: camera.zoom,
  b: 0,
  c: 0,
  d: camera.zoom,
  e: -camera.position.x * camera.zoom,
  f: -camera.position.y * camera.zoom,
});

/** Converts a world-space rectangle to its on-screen rectangle. */
export const worldRectToScreenRect = (rect: Rect, camera: Camera): Rect => {
  const origin = worldToScreen({ x: rect.x, y: rect.y }, camera);
  return { x: origin.x, y: origin.y, width: rect.width * camera.zoom, height: rect.height * camera.zoom };
};

/** Converts a screen-space rectangle to the world region it covers. */
export const screenRectToWorldRect = (rect: Rect, camera: Camera): Rect => {
  const origin = screenToWorld({ x: rect.x, y: rect.y }, camera);
  return { x: origin.x, y: origin.y, width: rect.width / camera.zoom, height: rect.height / camera.zoom };
};

export const boundsToRect = (b: Bounds): Rect => ({
  x: b.minX,
  y: b.minY,
  width: b.maxX - b.minX,
  height: b.maxY - b.minY,
});

export const rectToBounds = (r: Rect): Bounds => ({
  minX: r.x,
  minY: r.y,
  maxX: r.x + r.width,
  maxY: r.y + r.height,
});
